import React, { useState, useEffect, useRef } from 'react';
import './NotificationBell.css';

export default function NotificationBell({ token, compact = false }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const panelRef = useRef(null);

  // 获取未读数量
  const fetchUnreadCount = async () => {
    try {
      const res = await fetch('/api/notifications/unread-count', {
        headers: { 'x-token': token }
      });
      const data = await res.json();
      const newCount = data.count || 0;
      if (newCount > unreadCount && unreadCount >= 0) {
        // 有新消息时开始闪烁
        setIsNew(true);
      }
      if (newCount === 0) {
        // 所有消息已读，停止闪烁
        setIsNew(false);
      }
      setUnreadCount(newCount);
    } catch (e) { console.error(e); }
  };

  // 获取通知列表
  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications?limit=20', {
        headers: { 'x-token': token }
      });
      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (e) { 
      console.error(e); 
      setNotifications([]);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // 30秒轮询
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (showPanel) fetchNotifications();
  }, [showPanel]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 标记已读
  const markAsRead = async (id) => {
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: { 'x-token': token }
      });
      fetchUnreadCount();
      fetchNotifications();
    } catch (e) { console.error(e); }
  };

  // 全部已读
  const markAllRead = async () => {
    try {
      await fetch('/api/notifications/read-all', {
        method: 'PUT',
        headers: { 'x-token': token }
      });
      setUnreadCount(0);
      setIsNew(false);
      fetchNotifications();
    } catch (e) { console.error(e); }
  };

  // 删除通知
  const deleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'x-token': token }
      });
      fetchUnreadCount();
      fetchNotifications();
    } catch (e) { console.error(e); }
  };

  // 点击通知（跳转到相关日志）
  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id);
    // 点击消息后停止闪烁
    setIsNew(false);
    if (notification.type === 'annotation' && notification.related_id) {
      // 可以在这里跳转到日志详情或滚动到日志
      setShowPanel(false);
      // TODO: 可以触发滚动到对应日志
    }
  };

  // 格式化时间
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  // 点击铃铛
  const handleBellClick = () => {
    setShowPanel(!showPanel);
    if (!showPanel) {
      // 打开面板时停止闪烁
      setIsNew(false);
    }
  };

  return (
    <div className={`notification-bell-wrapper ${compact ? 'compact' : ''}`} ref={panelRef}>
      <button 
        className={`notification-bell-btn ${isNew && compact ? 'pulse' : ''}`}
        onClick={handleBellClick}
      >
        🔔
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {showPanel && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <h3>消息通知</h3>
            {unreadCount > 0 && (
              <button className="mark-all-read-btn" onClick={markAllRead}>
                全部已读
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">暂无通知</div>
            ) : (
              notifications.map(n => (
                <div 
                  key={n.id} 
                  className={`notification-item ${!n.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="notification-icon">
                    {n.type === 'annotation' ? '💬' : '📢'}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{n.title}</div>
                    <div className="notification-text">{n.content}</div>
                    <div className="notification-time">{formatTime(n.created_at)}</div>
                  </div>
                  <button 
                    className="notification-delete-btn"
                    onClick={(e) => deleteNotification(n.id, e)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
